/* Borderline winter 26/27 · brands (P7) behaviour, from winter-proto/assets/js/page-brands.js + page-brand.js (v83).
   Brand pages: text tabs (series, story) switch panels that Liquid already rendered: [data-blb-tabs] holds a
   role=tablist of buttons and .blb-panel[data-panel] siblings; arrow keys move along the tabs.
   Index: the wordmark grid's hairlines (each tile draws top + left; the row's last tile adds right, the last row
   adds bottom, so the empty board stays empty), the wall's balance (no department ends in a one-logo row), and the
   live counts (one Section Rendering request, sections/bl-brand-counts).
   Loaded as a module from each section that needs it: the browser runs it once. */

const ready = (fn) => (document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn, { once: true }) : fn());

/* ---------- tabs ---------- */
function selectTab(root, tab) {
  const tabs = [...root.querySelectorAll('[role="tab"]')];
  const i = tabs.indexOf(tab) + 1;
  if (i < 1) return;
  tabs.forEach((t) => {
    const on = t === tab;
    t.classList.toggle('is-on', on);
    t.setAttribute('aria-selected', on ? 'true' : 'false');
    t.tabIndex = on ? 0 : -1;
  });
  root.querySelectorAll('.blb-panel[data-panel]').forEach((p) => {
    p.hidden = p.dataset.panel !== String(i);
  });
}

function initTabs(scope) {
  scope.querySelectorAll('[data-blb-tabs]').forEach((root) => {
    if (root.dataset.blbTabsReady) return;
    root.dataset.blbTabsReady = '1';
    root.addEventListener('click', (e) => {
      const tab = e.target.closest('[role="tab"]');
      if (tab && root.contains(tab)) selectTab(root, tab);
    });
    root.addEventListener('keydown', (e) => {
      const tab = e.target.closest('[role="tab"]');
      if (!tab || !['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(e.key)) return;
      const tabs = [...root.querySelectorAll('[role="tab"]')];
      let i = tabs.indexOf(tab);
      if (e.key === 'ArrowRight') i = (i + 1) % tabs.length;
      if (e.key === 'ArrowLeft') i = (i - 1 + tabs.length) % tabs.length;
      if (e.key === 'Home') i = 0;
      if (e.key === 'End') i = tabs.length - 1;
      e.preventDefault();
      tabs[i].focus();
      tabs[i].scrollIntoView({ block: 'nearest', inline: 'nearest' });
      selectTab(root, tabs[i]);
    });
  });
}

/* ---------- index: grid hairlines ---------- */
function edges(scope) {
  scope.querySelectorAll('[data-blb-wm]').forEach((grid) => {
    const tiles = [...grid.querySelectorAll('.blb-wm__tile')];
    const cols = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length || 1;
    const n = tiles.length;
    tiles.forEach((t, i) => {
      t.classList.toggle('is-r', i % cols === cols - 1 || i === n - 1);
      t.classList.toggle('is-b', i + cols >= n);
    });
  });
}

/* ---------- index: no one-logo last rows in the wall ---------- */
function balance(scope) {
  scope.querySelectorAll('.blb-wall__names').forEach((wall) => {
    wall.querySelectorAll('.blb-wall__br').forEach((b) => b.remove());
    const items = [...wall.children];
    if (items.length < 3) return;
    const rowOf = (el) => {
      const r = el.getBoundingClientRect();
      return Math.round((r.top + r.bottom) / 2 / 24);
    };
    const last = rowOf(items[items.length - 1]);
    if (items.filter((el) => rowOf(el) === last).length === 1) {
      const br = document.createElement('span');
      br.className = 'blb-wall__br';
      br.setAttribute('aria-hidden', 'true');
      wall.insertBefore(br, items[items.length - 2]);
    }
  });
}

/* ---------- index: live counts ---------- */
async function counts(scope) {
  const root = scope.querySelector('[data-blb-index][data-counts]');
  if (!root || root.dataset.blbCounted) return;
  root.dataset.blbCounted = '1';
  try {
    const res = await fetch(root.dataset.counts, { credentials: 'same-origin' });
    if (!res.ok) return;
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const node = doc.querySelector('script[data-blb-counts]');
    if (!node) return;
    const map = JSON.parse(node.textContent || '{}');
    const lower = {};
    Object.keys(map).forEach((k) => { lower[k.toLowerCase()] = map[k]; });
    const one = root.dataset.one || '1 product';
    const many = root.dataset.many || '[count] products';
    root.querySelectorAll('.blb-wm__meta[data-vendor]').forEach((el) => {
      const n = lower[(el.dataset.vendor || '').toLowerCase()];
      if (n > 0) el.textContent = n === 1 ? one : many.replace('[count]', String(n));
    });
  } catch (e) {
    /* no counts is a quiet state: the tiles keep their names */
  }
}

function initIndex(scope) {
  if (!scope.querySelector('[data-blb-index]')) return;
  edges(scope);
  balance(scope);
  counts(scope);
}

let resizeT;
function onResize() {
  clearTimeout(resizeT);
  resizeT = setTimeout(() => {
    edges(document);
    balance(document);
  }, 150);
}

if (!window.__blBrand) {
  window.__blBrand = true;
  ready(() => {
    initTabs(document);
    initIndex(document);
  });
  /* the logos' widths are known once the images and the fonts are in */
  window.addEventListener('load', () => balance(document));
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => balance(document));
  window.addEventListener('resize', onResize);
  document.addEventListener('shopify:section:load', (e) => {
    initTabs(e.target);
    initIndex(e.target);
  });
}
